// Google Analytics on the public website, deferred until consent.
//
// The server opts a page in (AnalyticsOptIn): the HTML then carries
// <meta name="mektebi-analytics" content="G-…"> and, on Inertia pages, an
// `analytics: true` prop. Nothing reaches Google until the visitor's stored
// choice is "granted" (or they press Accept); only then is gtag.js injected.
//
// - gtag.js is injected only into a document the server opted in (the meta tag),
//   because only that document's CSP allows Google's hosts.
// - No automatic page views, Google signals or ad personalisation.
// - A page_view is sent by hand, and only for pages whose `analytics` is true;
//   on every other page GA is switched off (window["ga-disable-<id>"]), so even
//   GA's own history listener sends nothing while someone is on, say, sign-in.
// - page_location and page_referrer are origin + path: no query string or hash
//   (reset tokens, search terms) ever leaves.
export const CONSENT_KEY = "cookie-consent"
const SCRIPT_HOST = "https://www.googletagmanager.com"

type Gtag = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

type State = { id: string | null; loaded: boolean; current: boolean; lastLocation: string | null }
const state: State = { id: null, loaded: false, current: false, lastLocation: null }

export function storedConsent(): string | null {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

// Origin + path of a URL; "" for anything that isn't an http(s) URL.
export function cleanUrl(url: string | null | undefined): string {
  if (!url) return ""
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return ""
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return ""
  }
}

function measurementId(): string | null {
  const content = document.querySelector<HTMLMetaElement>('meta[name="mektebi-analytics"]')?.content
  return content && /^G-[A-Z0-9]+$/.test(content) ? content : null
}

function setDisabled(disabled: boolean) {
  if (state.id) (window as unknown as Record<string, unknown>)[`ga-disable-${state.id}`] = disabled
}

function load() {
  if (state.loaded || !state.id) return
  state.loaded = true
  window.dataLayer = window.dataLayer || []
  // gtag.js reads the `arguments` object itself, not an array.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag("js", new Date())
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "granted",
  })
  window.gtag("config", state.id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: cleanUrl(window.location.href),
    page_referrer: cleanUrl(document.referrer),
  })

  const script = document.createElement("script")
  script.async = true
  script.src = `${SCRIPT_HOST}/gtag/js?id=${encodeURIComponent(state.id)}`
  document.head.appendChild(script)
}

// The page now showing; `analytics` is the server's allowlist answer for it.
// The same page again (Inertia's "initial" navigate event, a form re-rendered
// with errors) is not counted twice.
export function trackPage(analytics: boolean) {
  const location = cleanUrl(window.location.href)
  const repeat = location === state.lastLocation
  const referrer = state.lastLocation ?? cleanUrl(document.referrer)
  state.current = analytics
  state.lastLocation = location
  if (!state.loaded || !state.id) return

  setDisabled(!analytics)
  if (!analytics || repeat) return
  window.gtag?.("event", "page_view", { page_location: location, page_referrer: referrer, page_title: document.title })
}

// Accept (or a stored "granted") loads GA and counts the current page;
// Decline or withdrawing stops it for the rest of this page's life.
export function applyConsent(value: string | null) {
  if (!state.id) return
  if (value === "granted") {
    const wasLoaded = state.loaded
    load()
    if (!wasLoaded) {
      state.lastLocation = null
      trackPage(state.current)
    } else {
      setDisabled(!state.current)
    }
    return
  }
  if (state.loaded) {
    window.gtag?.("consent", "update", { analytics_storage: "denied" })
    setDisabled(true)
    clearGaCookies()
  }
}

// Removes GA's first-party cookies (_ga, _ga_<id>) for this host and its parents.
function clearGaCookies() {
  const names = document.cookie.split(";").map((part) => part.split("=")[0].trim()).filter((name) => name === "_ga" || name.startsWith("_ga_"))
  const parts = window.location.hostname.split(".")
  const domains = parts.map((_, index) => parts.slice(index).join(".")).filter((domain) => domain.includes("."))
  names.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/`
    domains.forEach((domain) => (document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`))
  })
}

// Called once per document, before initCookieConsent. The meta tag is rendered
// exactly when the first page's `analytics` prop is true, so it answers for the
// first page; Inertia visits after it call trackPage with their own prop.
export function initAnalytics() {
  state.id = measurementId()
  state.loaded = false
  state.lastLocation = null
  state.current = state.id !== null
  if (!state.id) return
  if (storedConsent() === "granted") applyConsent("granted")
}
